// Phase 15 Plan 09 Task 3 — RoundTabStrip (R1..R5 status-discriminated tabs).
//
// 5 tabs (R1..R5) per DEC-012 Swiss-5. Per-tab visual states derived from
// the round's RoundPairing.status:
//   - Active (selectedRound === round): Sun-Yellow 3px underline + aria-current="page"
//   - Completed (status='completed'): Lime ✓ glyph appended to label; muted bg
//   - Future (status='future'): muted text, aria-disabled=true, non-tappable
//   - Bye (status='bye'): "bye" suffix, non-tappable (the caller's bye round)
//   - Pending (default for current round): standard label
//
// Sizing: 5 tabs at 44px each + 4 × 4px gaps = 236px total → fits 320px mobile.
//
// IMPORTANT: not using the Seg primitive directly — Seg's radiogroup
// semantics don't carry a per-option disabled state. RoundTabStrip is a
// `<nav role="tablist">` of `<button role="tab">` instead (W3C ARIA Tabs
// pattern). The visual recipe still echoes Phase 14's chunky pills, but the
// a11y semantics are tab-based since each tab maps to a distinct round body.

import type { JSX } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import type { RoundPairing } from '../../net/tournaments.js'

type Round = 1 | 2 | 3 | 4 | 5

export interface RoundTabStripProps {
  readonly rounds: readonly RoundPairing[]
  readonly selectedRound: Round
  readonly onSelectRound: (round: Round) => void
}

function statusToCellStyles(opts: {
  status: RoundPairing['status']
  isSelected: boolean
}): React.CSSProperties {
  const { status, isSelected } = opts
  const base: React.CSSProperties = {
    width: '44px',
    height: '44px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--color-paper-2)',
    boxShadow: 'var(--shadow-tile)',
    borderRadius: 'var(--radius-tile)',
    fontFamily: 'var(--font-family-display)',
    fontWeight: 700,
    fontSize: 'var(--font-size-14)',
    color: 'var(--color-text-primary)',
    cursor: 'pointer',
    padding: 0,
  }
  if (isSelected) {
    return {
      ...base,
      borderBottom: '3px solid var(--color-cta-primary)',
    }
  }
  if (status === 'completed') {
    return {
      ...base,
      backgroundColor: 'var(--color-surface-deep)',
      color: 'var(--color-cta-confirm)',
    }
  }
  if (status === 'future') {
    return {
      ...base,
      color: 'var(--color-text-tertiary)',
      cursor: 'not-allowed',
      opacity: 0.6,
    }
  }
  if (status === 'bye') {
    return {
      ...base,
      color: 'var(--color-text-tertiary)',
      cursor: 'not-allowed',
    }
  }
  return base
}

export function RoundTabStrip(props: RoundTabStripProps): JSX.Element {
  const { rounds, selectedRound, onSelectRound } = props
  const intl = useIntl()

  // ARIA tablist semantics: we apply the `tablist` role to a generic <div>
  // rather than <nav> per the W3C ARIA Tabs pattern
  // (https://www.w3.org/WAI/ARIA/apg/patterns/tabs/). A <nav> + role=tablist
  // would collide with <nav>'s implicit role=navigation.
  return (
    <div
      data-testid="round-tab-strip"
      role="tablist"
      aria-label={intl.formatMessage({ id: 'social.tournaments.rounds_aria' })}
      style={{
        display: 'flex',
        height: '44px',
        gap: 'var(--space-1)',
        alignItems: 'center',
      }}
    >
      {([1, 2, 3, 4, 5] as const).map((round) => {
        const data = rounds[round - 1]
        const status: RoundPairing['status'] = data?.status ?? 'future'
        const isSelected = selectedRound === round
        const isDisabled = status === 'future' || status === 'bye'
        const handleClick = (): void => {
          if (isDisabled) return
          onSelectRound(round)
        }
        const cellStyle = statusToCellStyles({ status, isSelected })
        const ariaProps: Record<string, string> = {
          role: 'tab',
          'data-round': String(round),
          'data-status': status,
          'aria-selected': isSelected ? 'true' : 'false',
        }
        if (isSelected) ariaProps['aria-current'] = 'page'
        if (isDisabled) ariaProps['aria-disabled'] = 'true'
        return (
          <button
            key={round}
            type="button"
            {...ariaProps}
            disabled={isDisabled}
            onClick={handleClick}
            style={cellStyle}
          >
            {/* WR-11: drop the conditional `aria-hidden` on the label —
                hiding the round number from assistive tech on bye rounds
                also hid the round identity itself. Sighted users see "R1"
                + a bye chip; screen-reader users should hear the same. */}
            <span>
              <FormattedMessage
                id="social.tournaments.round"
                defaultMessage="R{n}"
                values={{ n: round }}
              />
            </span>
            {status === 'completed' ? (
              <span
                aria-hidden="true"
                style={{
                  marginLeft: '2px',
                  color: 'var(--color-cta-confirm)',
                  fontSize: 'var(--font-size-14)',
                }}
              >
                {'✓'}
              </span>
            ) : null}
            {status === 'bye' ? (
              <span
                style={{
                  marginLeft: '2px',
                  fontSize: 'var(--font-size-10)',
                  fontFamily: 'var(--font-family-display)',
                  fontWeight: 700,
                  color: 'var(--color-text-tertiary)',
                  textTransform: 'uppercase',
                }}
              >
                <FormattedMessage
                  id="social.tournaments.round_bye"
                  defaultMessage="bye"
                />
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}
